import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Checkbox,
    FormGroup,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Chip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Autocomplete,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { featuresApi, billingApi } from '../services/api';
import { FEATURES, PLATFORM_FEATURES, KNOWN_PLATFORMS } from '../constants/features';
import { COUNTRIES } from '../constants/countries';

// ── Existing override entry row ───────────────────────────────────────────────
const OverrideEntry = ({ entryKey, val, onToggle, onDelete }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>{entryKey}</Typography>
        <FormControlLabel
            control={<Switch checked={val} onChange={(e) => onToggle(e.target.checked)} size="small" />}
            label={<Typography variant="caption" sx={{ fontWeight: 700, color: val ? 'success.main' : 'error.main' }}>{val ? 'ON' : 'OFF'}</Typography>}
            sx={{ m: 0 }}
        />
        <IconButton size="small" color="error" onClick={onDelete} sx={{ p: 0.5 }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
    </Box>
);

// ── Plan overrides: dropdown select ──────────────────────────────────────────
const PlanOverrideList = ({ feature, data, plans, handleAddOverride, handleRemoveOverride }) => {
    const [selectedPlan, setSelectedPlan] = useState('');
    const [newVal, setNewVal] = useState(true);
    const existing = data || {};
    const available = plans.filter(p => !(p in existing));

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                Plan Overrides
            </Typography>

            {Object.entries(existing).map(([key, val]) => (
                <OverrideEntry
                    key={key}
                    entryKey={key}
                    val={val}
                    onToggle={(v) => handleAddOverride(feature, 'plan_overrides', key, v)}
                    onDelete={() => handleRemoveOverride(feature, 'plan_overrides', key)}
                />
            ))}

            {available.length > 0 ? (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Select plan</InputLabel>
                        <Select value={selectedPlan} label="Select plan" onChange={(e) => setSelectedPlan(e.target.value)}>
                            {available.map(p => (
                                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                        <FormControlLabel
                            control={<Switch checked={newVal} onChange={(e) => setNewVal(e.target.checked)} size="small" />}
                            label={<Typography variant="caption" sx={{ fontWeight: 700, color: newVal ? 'success.main' : 'error.main' }}>{newVal ? 'Enabled' : 'Disabled'}</Typography>}
                            sx={{ m: 0 }}
                        />
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={!selectedPlan}
                            onClick={() => {
                                handleAddOverride(feature, 'plan_overrides', selectedPlan, newVal);
                                setSelectedPlan('');
                            }}
                        >
                            Add Override
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    All plans configured
                </Typography>
            )}
        </Box>
    );
};

// ── Country overrides: searchable autocomplete multi-select ───────────────────
const CountryOverrideList = ({ feature, data, handleAddOverride, handleRemoveOverride }) => {
    const [selected, setSelected] = useState([]);
    const [newVal, setNewVal] = useState(true);
    const existing = data || {};
    const existingCodes = Object.keys(existing);
    const available = COUNTRIES.filter(c => !existingCodes.includes(c.code));

    const handleAdd = () => {
        selected.forEach(c => handleAddOverride(feature, 'country_overrides', c.code, newVal));
        setSelected([]);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                Country Overrides
            </Typography>

            {Object.entries(existing).map(([code, val]) => {
                const country = COUNTRIES.find(c => c.code === code);
                return (
                    <OverrideEntry
                        key={code}
                        entryKey={country ? `${country.label} (${code})` : code}
                        val={val}
                        onToggle={(v) => handleAddOverride(feature, 'country_overrides', code, v)}
                        onDelete={() => handleRemoveOverride(feature, 'country_overrides', code)}
                    />
                );
            })}

            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                <Autocomplete
                    multiple
                    size="small"
                    options={available}
                    value={selected}
                    onChange={(_, v) => setSelected(v)}
                    getOptionLabel={(o) => `${o.label} (${o.code})`}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    renderInput={(params) => <TextField {...params} placeholder="Search countries…" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={option.code} label={`${option.code}`} size="small" {...getTagProps({ index })} />
                        ))
                    }
                    fullWidth
                    disableCloseOnSelect
                    limitTags={4}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <FormControlLabel
                        control={<Switch checked={newVal} onChange={(e) => setNewVal(e.target.checked)} size="small" />}
                        label={<Typography variant="caption" sx={{ fontWeight: 700, color: newVal ? 'success.main' : 'error.main' }}>{newVal ? 'Enabled' : 'Disabled'}</Typography>}
                        sx={{ m: 0 }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={selected.length === 0}
                        onClick={handleAdd}
                    >
                        Add{selected.length > 0 ? ` ${selected.length} ${selected.length === 1 ? 'Country' : 'Countries'}` : ' Countries'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// ── User overrides: free-text UID input ───────────────────────────────────────
const UserOverrideList = ({ feature, data, handleAddOverride, handleRemoveOverride }) => {
    const [newUid, setNewUid] = useState('');
    const [newVal, setNewVal] = useState(true);
    const existing = data || {};

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                User UID overrides bypass all other rules. Paste exact UIDs from the Firebase Console.
            </Alert>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                Specific User Overrides
            </Typography>

            {Object.entries(existing).map(([uid, val]) => (
                <OverrideEntry
                    key={uid}
                    entryKey={uid}
                    val={val}
                    onToggle={(v) => handleAddOverride(feature, 'user_overrides', uid, v)}
                    onDelete={() => handleRemoveOverride(feature, 'user_overrides', uid)}
                />
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField
                    size="small"
                    placeholder="Paste User UID"
                    value={newUid}
                    onChange={(e) => setNewUid(e.target.value)}
                    sx={{ flex: 1, fontFamily: 'monospace' }}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <FormControlLabel
                    control={<Switch checked={newVal} onChange={(e) => setNewVal(e.target.checked)} size="small" />}
                    label={<Typography variant="caption" sx={{ fontWeight: 700, color: newVal ? 'success.main' : 'error.main' }}>{newVal ? 'ON' : 'OFF'}</Typography>}
                    sx={{ m: 0 }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!newUid.trim()}
                    onClick={() => {
                        handleAddOverride(feature, 'user_overrides', newUid.trim(), newVal);
                        setNewUid('');
                    }}
                >
                    Add
                </Button>
            </Box>
        </Box>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminToggles() {
    const [toggles, setToggles] = useState({});
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => { fetchToggles(); }, []);

    const fetchToggles = async () => {
        try {
            setLoading(true);
            const [data, billingConfig] = await Promise.all([
                featuresApi.getAdminToggles(),
                billingApi.getConfig(),
            ]);
            setPlans(Object.keys(billingConfig.plans || {}));
            const normalized = {};
            Object.values(FEATURES).forEach(feat => {
                const existing = data[feat] || {};
                normalized[feat] = {
                    global: existing.global !== false,
                    country_overrides: existing.country_overrides || {},
                    plan_overrides: existing.plan_overrides || {},
                    user_overrides: existing.user_overrides || {},
                    ...(PLATFORM_FEATURES.includes(feat) && {
                        allowed_platforms: existing.allowed_platforms || KNOWN_PLATFORMS,
                    }),
                };
            });
            setToggles(normalized);
        } catch (err) {
            setError(err.message || 'Failed to load toggles');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');
            await featuresApi.updateAdminToggles(toggles);
            setSuccess('Toggles saved successfully');
        } catch (err) {
            setError(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleGlobalToggle = (feature) => {
        setToggles(prev => ({
            ...prev,
            [feature]: { ...prev[feature], global: !prev[feature].global },
        }));
    };

    const handleAddOverride = (feature, overrideType, key, val) => {
        if (!key.trim()) return;
        setToggles(prev => ({
            ...prev,
            [feature]: {
                ...prev[feature],
                [overrideType]: { ...(prev[feature][overrideType] || {}), [key.trim()]: val },
            },
        }));
    };

    const handlePlatformToggle = (feature, platform) => {
        setToggles(prev => {
            const current = prev[feature].allowed_platforms || KNOWN_PLATFORMS;
            const updated = current.includes(platform)
                ? current.filter(p => p !== platform)
                : [...current, platform];
            if (updated.length === 0) return prev;
            return { ...prev, [feature]: { ...prev[feature], allowed_platforms: updated } };
        });
    };

    const handleRemoveOverride = (feature, overrideType, key) => {
        setToggles(prev => {
            const newOverrides = { ...prev[feature][overrideType] };
            delete newOverrides[key];
            return { ...prev, [feature]: { ...prev[feature], [overrideType]: newOverrides } };
        });
    };

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ maxWidth: 860, mx: 'auto', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4">Admin: Feature Toggles</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                >
                    Save Changes
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            {Object.keys(toggles).map(feature => (
                <Card key={feature} sx={{ mb: 4, p: 2, borderRadius: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                                {feature.replace(/_/g, ' ')}
                            </Typography>
                            <FormControlLabel
                                control={<Switch checked={toggles[feature].global} onChange={() => handleGlobalToggle(feature)} />}
                                label={toggles[feature].global ? 'Globally Enabled' : 'Globally Disabled'}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {/* Platform selector — only for posting, engagement, analytics */}
                        {PLATFORM_FEATURES.includes(feature) && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Allowed Platforms
                                </Typography>
                                <FormGroup row>
                                    {KNOWN_PLATFORMS.map(platform => (
                                        <FormControlLabel
                                            key={platform}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={(toggles[feature].allowed_platforms || KNOWN_PLATFORMS).includes(platform)}
                                                    onChange={() => handlePlatformToggle(feature, platform)}
                                                />
                                            }
                                            label={
                                                <Chip
                                                    label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                                                    size="small"
                                                    variant={(toggles[feature].allowed_platforms || KNOWN_PLATFORMS).includes(platform) ? 'filled' : 'outlined'}
                                                    color="primary"
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            }
                                        />
                                    ))}
                                </FormGroup>
                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                            <Box>
                                <PlanOverrideList
                                    feature={feature}
                                    data={toggles[feature].plan_overrides}
                                    plans={plans}
                                    handleAddOverride={handleAddOverride}
                                    handleRemoveOverride={handleRemoveOverride}
                                />
                                <Divider sx={{ my: 1 }} />
                                <CountryOverrideList
                                    feature={feature}
                                    data={toggles[feature].country_overrides}
                                    handleAddOverride={handleAddOverride}
                                    handleRemoveOverride={handleRemoveOverride}
                                />
                            </Box>
                            <Box>
                                <UserOverrideList
                                    feature={feature}
                                    data={toggles[feature].user_overrides}
                                    handleAddOverride={handleAddOverride}
                                    handleRemoveOverride={handleRemoveOverride}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}
